import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import NavMenu from '../components/ui/nav-menu';
import FooterNav from '../components/ui/footer-nav';
import ModuleRail from '../components/ui/module-rail';
import HeaderTasksButton from '../components/ui/header-tasks-button';
import { ArrowLeft, ShieldCheck, Loader2, Truck } from 'lucide-react';
import { toast } from 'sonner';

// DELIVER THE WORK = Blue theme
const STAGE = {
  bar: 'bg-blue-500',
  label: 'text-blue-300',
  button: 'bg-blue-600 hover:bg-blue-700',
};

const GenerateSWMS = () => {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  
  const [formData, setFormData] = useState({
    clientName: '',
    siteAddress: '',
    tradeType: '',
    jobDescription: '',
    workDate: '',
    specialRisks: '',
    workerNames: ''
  });

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleGenerateSWMS = async () => {
    if (!formData.clientName.trim()) { toast.error('Enter client name'); return; }
    if (!formData.siteAddress.trim()) { toast.error('Enter site address'); return; }
    if (!formData.tradeType.trim()) { toast.error('Enter trade type'); return; }
    if (!formData.jobDescription.trim()) { toast.error('Enter work description'); return; }

    setGenerating(true);
    try {
      const webhookUrl = process.env.REACT_APP_SWMS_WEBHOOK_URL;
      const response = await axios.post(webhookUrl, formData, { headers: { 'Content-Type': 'application/json' } });
      
      if (response.data.success === false) {
        toast.error('Failed to generate SWMS');
        return;
      }
      
      if (response.data.documentText) {
        sessionStorage.setItem('swmsResult', JSON.stringify({ ...response.data, workerNames: formData.workerNames }));
        toast.success('SWMS generated!');
        navigate('/tradie/swms-result');
      } else if (response.data.message === 'Workflow was started') {
        const placeholderResult = {
          success: true,
          documentType: 'SWMS',
          title: `SWMS - ${formData.tradeType}`,
          ...formData,
          documentText: `SAFE WORK METHOD STATEMENT (SWMS)\n\nClient: ${formData.clientName}\nSite: ${formData.siteAddress}\nTrade: ${formData.tradeType}\nDate: ${formData.workDate}\nWorkers: ${formData.workerNames}\n\nWORK DESCRIPTION:\n${formData.jobDescription}\n\nIDENTIFIED RISKS:\n${formData.specialRisks || 'None specified'}\n\n---\nNote: Draft SWMS. Full document being generated.`,
          createdAt: new Date().toISOString()
        };
        sessionStorage.setItem('swmsResult', JSON.stringify(placeholderResult));
        toast.success('SWMS submitted!');
        navigate('/tradie/swms-result');
      } else {
        sessionStorage.setItem('swmsResult', JSON.stringify({ ...response.data, workerNames: formData.workerNames }));
        toast.success('SWMS generated!');
        navigate('/tradie/swms-result');
      }
    } catch (error) {
      toast.error('Connection failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="bg-[#1F2A37] text-white p-4 sticky top-0 z-10">
        <div className={`h-1 ${STAGE.bar} absolute top-0 left-0 right-0`}></div>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Truck className="w-7 h-7 text-blue-400" />
            <div>
              <h1 className="text-lg font-bold">SWMS</h1>
              <p className={`${STAGE.label} text-xs font-medium`}>DELIVER WORK</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <HeaderTasksButton stage="deliver" />
            <NavMenu />
          </div>
        </div>
      </header>

      {/* Module Rail */}
      <ModuleRail stage="deliver" activeModule="swms" />

      {/* Form */}
      <main className="p-3 max-w-lg mx-auto pb-6">
        <Card className="border-2 border-slate-200">
          <CardContent className="p-4 space-y-4">
            
            <p className="text-slate-600 text-sm">Create a SWMS from site details.</p>

            {/* Client */}
            <div className="space-y-1">
              <Label className="text-sm font-bold text-slate-700">Client / Builder <span className="text-red-500">*</span></Label>
              <Input data-testid="client-name-input" placeholder="Client name" value={formData.clientName} onChange={(e) => handleChange('clientName', e.target.value)} className="h-11 border-2" />
            </div>

            {/* Site */}
            <div className="space-y-1">
              <Label className="text-sm font-bold text-slate-700">Site address <span className="text-red-500">*</span></Label>
              <Input data-testid="site-address-input" placeholder="e.g. 12 Smith St, Sydney" value={formData.siteAddress} onChange={(e) => handleChange('siteAddress', e.target.value)} className="h-11 border-2" />
            </div>

            {/* Trade */}
            <div className="space-y-1">
              <Label className="text-sm font-bold text-slate-700">Trade type <span className="text-red-500">*</span></Label>
              <Input data-testid="trade-type-input" placeholder="e.g. Tiling" value={formData.tradeType} onChange={(e) => handleChange('tradeType', e.target.value)} className="h-11 border-2" />
            </div>

            {/* Description */}
            <div className="space-y-1">
              <Label className="text-sm font-bold text-slate-700">Work description <span className="text-red-500">*</span></Label>
              <Textarea data-testid="job-description-input" placeholder="e.g. Wall and floor tiling to bathroom" value={formData.jobDescription} onChange={(e) => handleChange('jobDescription', e.target.value)} className="min-h-[80px] text-base border-2" />
            </div>

            {/* Date */}
            <div className="space-y-1">
              <Label className="text-sm font-bold text-slate-700">Date prepared</Label>
              <Input data-testid="work-date-input" type="date" value={formData.workDate} onChange={(e) => handleChange('workDate', e.target.value)} className="h-11 border-2" />
            </div>

            {/* Risks */}
            <div className="space-y-1">
              <Label className="text-sm font-bold text-slate-700">Risks / Site notes</Label>
              <Textarea data-testid="special-risks-input" placeholder="e.g. Dust, manual handling, wet areas" value={formData.specialRisks} onChange={(e) => handleChange('specialRisks', e.target.value)} className="min-h-[60px] border-2" />
            </div>

            {/* Workers */}
            <div className="space-y-1">
              <Label className="text-sm font-bold text-slate-700">Workers & roles</Label>
              <Textarea data-testid="worker-names-input" placeholder="e.g.&#10;John Smith, Supervisor&#10;Sam Lee, Worker" value={formData.workerNames} onChange={(e) => handleChange('workerNames', e.target.value)} className="min-h-[80px] border-2" />
              <p className="text-xs text-slate-500">First name will sign the SWMS. One per line.</p>
            </div>

            {/* Submit */}
            <Button type="button" data-testid="generate-swms-btn" onClick={handleGenerateSWMS} disabled={generating} className={`w-full h-12 ${STAGE.button} text-white text-base font-bold`}>
              {generating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating...</> : <><ShieldCheck className="w-5 h-5 mr-2" strokeWidth={2} />Generate SWMS</>}
            </Button>

          </CardContent>
        </Card>
      </main>
      
      <FooterNav />
    </div>
  );
};

export default GenerateSWMS;
